import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { BellRinging, BellSlash, DeviceMobile, WarningCircle, PaperPlaneTilt } from "@phosphor-icons/react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export default function PushSetup() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [publicKey, setPublicKey] = useState("");
  const [configured, setConfigured] = useState(false);
  const [subCount, setSubCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const s = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(s);
    if (!s) return;
    setPermission(Notification.permission);
    try {
      const [k, st] = await Promise.all([
        api.get("/push/public-key"),
        api.get("/push/status"),
      ]);
      setPublicKey(k.data.public_key);
      setConfigured(k.data.configured);
      setSubCount(st.data.active_subscriptions);
    } catch { /* not configured */ }
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { toast.error("Autorisation refusée"); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      toast.success("Notifications activées sur cet appareil");
      load();
    } catch (e) {
      toast.error(e.message || "Erreur d'abonnement");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      } else {
        await api.delete("/push/subscribe", { data: {} });
      }
      toast.success("Notifications désactivées");
      load();
    } catch (e) { toast.error(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test");
      if (r.data.delivered > 0) toast.success(`Notification test envoyée à ${r.data.delivered} appareil(s)`);
      else toast.error("Aucun appareil abonné — activez d'abord les notifications");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!supported) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center gap-3">
          <WarningCircle size={20} className="text-orange-500 shrink-0"/>
          <div>
            <div className="font-medium">Navigateur non compatible</div>
            <div className="text-xs text-muted-foreground">Utilisez Chrome, Edge, Firefox, Brave ou Safari (iOS 16.4+) et installez MasterTicket sur votre écran d&apos;accueil.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!configured) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center gap-3">
          <WarningCircle size={20} className="text-orange-500 shrink-0"/>
          <div>
            <div className="font-medium">Push non configuré côté serveur</div>
            <div className="text-xs text-muted-foreground">Ajoutez VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY, VAPID_SUBJECT dans backend/.env puis redémarrez le service.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const active = subCount > 0 && permission === "granted";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary"}`}>
              {active ? <BellRinging size={22} weight="duotone"/> : <BellSlash size={22} weight="duotone"/>}
            </div>
            <div className="min-w-0">
              <div className="font-medium">Notifications push</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Recevez une alerte instantanée sur cet appareil pour les nouvelles affectations, tickets urgents et SLA dépassés — même quand MasterTicket n&apos;est pas ouvert.
              </p>
              {active && (
                <div className="text-xs text-emerald-600 font-medium mt-1.5 inline-flex items-center gap-1">
                  <DeviceMobile size={12}/> {subCount} appareil(s) abonné(s)
                </div>
              )}
              {permission === "denied" && (
                <div className="text-xs text-destructive mt-1.5">Autorisation bloquée — modifiez les paramètres du site dans votre navigateur.</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              data-testid="push-toggle"
              checked={active} disabled={busy || permission === "denied"}
              onCheckedChange={(v) => v ? enable() : disable()}
            />
            {active && (
              <Button size="sm" variant="secondary" onClick={test} disabled={busy} data-testid="push-test">
                <PaperPlaneTilt size={14} className="mr-1.5"/> Tester
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
